import React, { useRef } from 'react'
import { observer, inject } from 'mobx-react'
import classnames from 'classnames'
import { withTranslation } from 'react-i18next'
import _ from 'lodash'
import { HugeiconsIcon } from '@hugeicons/react'
import { FilterHorizontalIcon, PlusSignIcon, Search01Icon } from '@hugeicons/core-free-icons'
import SortIcon from '../common/icons/SortIcon'
import ChooseAddTypeModal from './ChooseAddTypeModal'
import SortPopup from './SortPopup'
import FilterPopup from './FilterPopup'
import QuickFilter, { StatusQuickFilter, TagQuickFilter, TypeQuickFilter } from './QuickFilter'
import HiddenEyeIcon from '../common/icons/HiddenEyeIcon'
import EyeIcon from '../common/icons/EyeIcon'
import ProBadge from '../common/ProBadge'
import { startProCheckout } from '../../proCheckout'
import useClickOutside from '../common/useClickOutside'

const debouncedFilterAndExpand = _.debounce((query, uiStore) => {
  if (!query) {
    uiStore.clearFilters()
    return
  }

  if (query.trim()) {
    uiStore.applyFilters()
    uiStore.expandTargetGroups()
    uiStore.resetBounds()
  }
}, 300)

const MapItemControls = ({ t, uiStore, dataStore, selectionStore }) => {
  const addMenuRef = useRef(null)
  const filterMenuRef = useRef(null)
  const sortMenuRef = useRef(null)

  useClickOutside(addMenuRef, () => {
    if (uiStore.isAddChoiceTypeVisible) {
      const map = uiStore && uiStore.map && uiStore.map.leafletElement
      const currentBounds = map && typeof map.getBounds === 'function' ? map.getBounds() : null
      if (uiStore && typeof uiStore.suppressMapBoundsTemporarily === 'function') {
        uiStore.suppressMapBoundsTemporarily(1000)
      }
      if (uiStore && currentBounds && typeof uiStore.setBoundsHelper === 'function') {
        uiStore.setBoundsHelper(currentBounds)
      }
      uiStore.hideChooseAddType()
      if (typeof uiStore.restorePreviousSelectionBeforeAdd === 'function') {
        uiStore.restorePreviousSelectionBeforeAdd()
      }
    }
  }, uiStore.isAddChoiceTypeVisible)
  useClickOutside(filterMenuRef, () => uiStore.hideFilterPopup(), uiStore.isFilterPopupVisible)
  useClickOutside(sortMenuRef, () => uiStore.hideSortPopup(), uiStore.isSortPopupVisible)

  const handleNewItemClick = () => {
    if (uiStore.hasReachedStarterRegionLimit) {
      return
    }
    if (typeof uiStore.snapshotSelectionBeforeAdd === 'function') {
      uiStore.snapshotSelectionBeforeAdd()
    }
    uiStore.isPOIMenuExpanded = false
    const map = uiStore && uiStore.map && uiStore.map.leafletElement
    const center = map && typeof map.getCenter === 'function' ? map.getCenter() : null
    const zoom = map && typeof map.getZoom === 'function' ? map.getZoom() : null
    const currentBounds = map && typeof map.getBounds === 'function' ? map.getBounds() : null
    if (uiStore && typeof uiStore.suppressMapBoundsTemporarily === 'function') {
      uiStore.suppressMapBoundsTemporarily(1000)
    }
    if (uiStore && currentBounds && typeof uiStore.setBoundsHelper === 'function') {
      uiStore.setBoundsHelper(currentBounds)
    }
    if (typeof uiStore.closeQuickCreate === 'function') {
      uiStore.closeQuickCreate()
    }
    if (typeof uiStore.closeQuickCreateModal === 'function') {
      uiStore.closeQuickCreateModal()
    }
    if (uiStore.map && uiStore.map.leafletElement && typeof uiStore.map.leafletElement.closePopup === 'function') {
      uiStore.map.leafletElement.closePopup()
    }
    if (typeof uiStore.closePoiTooltip === 'function') {
      uiStore.closePoiTooltip()
    }
    if (typeof selectionStore.hidePopupMenu === 'function') {
      selectionStore.hidePopupMenu()
    }
    if (typeof uiStore.hideColorsMenu === 'function') {
      uiStore.hideColorsMenu()
    }
    if (typeof uiStore.clearHoveredTargetGroup === 'function') {
      uiStore.clearHoveredTargetGroup()
    }
    uiStore.isAddChoiceTypeVisible = true
    if (map && center && zoom != null) {
      map.setView(center, zoom, { animate: false })
      setTimeout(() => map.setView(center, zoom, { animate: false }), 0)
    }
  }

  const starterLimitMessage = (
    <div
      style={{ fontFamily: "'General Sans', system-ui, sans-serif", textAlign: 'center', padding: '22px 24px 20px', boxSizing: 'border-box' }}
      onMouseEnter={() => uiStore.cancelHideHoverTooltip()}
      onMouseLeave={() => uiStore.hideHoverTooltip()}
    >
      <div style={{ fontWeight: 700, fontSize: '17px', color: '#111827', marginBottom: '10px' }}>
        You've mapped 5 territories.
      </div>
      <div style={{ fontSize: '13px', lineHeight: 1.5, color: '#4B5563', marginBottom: '20px' }}>
        Ready to take it further? Pro gives you unlimited territories, more POI searches, income data, and exports. Everything you need to make the right call.
      </div>
      <button
        type='button'
        onClick={() => startProCheckout({ uiStore, t })}
        style={{ width: '100%', height: '42px', borderRadius: '8px', border: 'none', backgroundColor: '#64cf5a', color: '#FFFFFF', fontWeight: 600, fontSize: '15px', cursor: 'pointer' }}
        onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#5fc556' }}
        onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#64cf5a' }}
      >
        Upgrade to Pro
      </button>
    </div>
  )

  const handleNewItemMouseOver = () => {
    if (uiStore.hasReachedStarterRegionLimit) {
      uiStore.cancelHideHoverTooltip()
      uiStore.openHoverTooltip({
        text: starterLimitMessage,
        selector: '.item-controls__add',
        style: { display: 'flex', width: '300px', main: { padding: 0 } },
      })
    }
  }

  const handleSearchInput = (event) => {
    uiStore.filters.searchQuery = event.target.value

    debouncedFilterAndExpand(event.target.value, uiStore)
  }

  const handleReset = () => {
    uiStore.clearFilters()
  }

  const handleTogglePopup = () => {
    if (uiStore.user.isStarter) {
      startProCheckout({ uiStore, t })
      return
    }
    uiStore.toggleFilterPopup()
  }

  return (
    <div className='item-controls'>
      <div className='item-controls__filters'>
        <div className='item-controls__search-holder'>
          <HugeiconsIcon icon={Search01Icon} size={16} color='#94A3B8' className='item-controls__search-icon' />
          <input
            className='item-controls__search'
            type='search'
            placeholder={t('MAP_FILTER.SEARCH', { defaultValue: 'Search In the List' })}
            value={uiStore.filters.searchQuery}
            onChange={handleSearchInput}
          />
        </div>
        <div className='item-controls__side-holder'>
          <div ref={filterMenuRef} className='item-controls__filter-holder'>
            <button
              type='button'
              className={classnames({
                'item-controls__action-btn': true,
                'item-controls__filter-btn': true,
                'item-controls__disabled': uiStore.user.isStarter,
                active: uiStore.isFilterPopupVisible
              })}
              onClick={handleTogglePopup}
              title='Filter'
            >
              <HugeiconsIcon icon={FilterHorizontalIcon} size={18} color='#334155' />
              {uiStore.user.isStarter && <ProBadge className='map-settings__pro text-small' />}
            </button>
            {uiStore.isFilterPopupVisible && <FilterPopup />}
          </div>
          <div ref={sortMenuRef} className='item-controls__sort-holder'>
            <button
              type='button'
              className={classnames({
                'item-controls__action-btn': true,
                'item-controls__sort-btn': true,
                active: uiStore.isSortPopupVisible
              })}
              onClick={() => uiStore.toggleSortPopup()}
              title='Sort'
            >
              <SortIcon />
            </button>
            {uiStore.isSortPopupVisible && <SortPopup />}
          </div>
          <div className='item-controls__hide-holder'>
            <button
              type='button'
              className='item-controls__action-btn item-controls__eye-btn'
              title='Show/Hide all of my target groups on map'
              onClick={(e) => {
                const newVisible = uiStore.targetGroupsHidden
                dataStore.myTargetGroups.forEach(group => {
                  group.visible = newVisible
                })
                uiStore.targetGroupsHidden = !newVisible
                e.stopPropagation()
              }}
            >
              {uiStore.targetGroupsHidden
                ? (
                  <HiddenEyeIcon
                    width='18'
                    height='18'
                  />
                  )
                : (
                  <EyeIcon
                    width='18'
                    height='18'
                  />
                  )}
            </button>
          </div>
        </div>
      </div>

      <div ref={addMenuRef} className='item-controls__add-holder'>
        <button
          type='button'
          className={classnames({
            'item-controls__add': true,
            'text-big': true,
            'item-controls__disabled': uiStore.hasReachedStarterRegionLimit,
            active: uiStore.isAddChoiceTypeVisible
          })}
          onClick={handleNewItemClick}
          onMouseOver={handleNewItemMouseOver}
          onMouseLeave={() => uiStore.scheduleHideHoverTooltip()}
        >
          <HugeiconsIcon icon={PlusSignIcon} size={18} color='#FFFFFF' className='item-controls__plus' />
          {t('GLOBAL.NEW', { defaultValue: 'New' })}
        </button>
        {uiStore.isAddChoiceTypeVisible && <ChooseAddTypeModal />}
      </div>
      <div className='item-contorls__quick-settings'>
        <TypeQuickFilter />
        <StatusQuickFilter />
        <TagQuickFilter />
        <QuickFilter name='population' label='P' filter={uiStore.filters.population} />
        <QuickFilter name='density' label='D' filter={uiStore.filters.density} />
        <QuickFilter name='avgAge' label='A' filter={uiStore.filters.avgAge} />
        <QuickFilter name='genderRatio' label='G' filter={uiStore.filters.genderRatio} />
        <QuickFilter name='income' label='I' filter={uiStore.filters.income} />
        {!uiStore.filters.isAtDefaults && <button type='button' className='underlined-btn item-controls__quick-reset' onClick={handleReset}>{t('MAP_FILTER.RESET')}</button>}
      </div>
    </div>
  )
}

export default withTranslation()(inject('uiStore', 'dataStore', 'selectionStore')(observer(MapItemControls)))
